import { useEffect, useMemo } from 'react';
import { useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { MailApi } from '@/api/contract';
import type { Account, SendResult, SentCopy, SentCopyRunResult } from '@/api/types';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';

/**
 * Copies of sent mail that are not in Sent yet (#84), for every mailbox.
 *
 * The server keeps a message whose APPEND to Sent failed and tries again with
 * backoff. With the mailbox's background credential it does that on its own;
 * without one it can only try while someone is signed in and asks, and this
 * hook is what asks: whenever a copy the server is NOT retrying unattended
 * comes due, it calls `run` with the session's credentials. Copies the server
 * does retry unattended are left to it, so two attempts do not race.
 *
 * Mounted on the shell (so the retry happens wherever the person is in the
 * app) and again by the Mail view's banner, which reads the same cache. The
 * runs are shared across both through {@link lastRun}, so two mounts never
 * run a mailbox twice.
 */

/** How often the lists are re-read while the tab is visible. */
export const SENT_COPIES_REFETCH_MS = 60_000;

/**
 * The least time between two runs for one mailbox. A run that failed (offline,
 * a 503), or a copy the server answers as still due because the two clocks
 * disagree, would otherwise be retried in a tight loop.
 */
const MIN_RUN_GAP_MS = 30_000;

/** The largest delay `setTimeout` honours; past it, it fires at once. */
const MAX_TIMER_MS = 2_147_483_647;

/** Mailbox id → when its last run started (epoch ms). Module-wide on purpose: see above. */
const lastRun = new Map<string, number>();
const running = new Set<string>();

/** Test hook: forgets the run bookkeeping between tests. */
export function resetSentCopyRuns(): void {
  lastRun.clear();
  running.clear();
}

export interface OpenSentCopy {
  account: Account;
  copy: SentCopy;
}

/** True for a copy this client should run once it is due. */
function clientRuns(copy: SentCopy): boolean {
  return copy.state === 'pending' && !copy.unattended && copy.nextAttemptAt !== undefined;
}

/** Epoch ms of the next copy this client has to run for, or undefined. */
function nextDue(copies: readonly SentCopy[]): number | undefined {
  let soonest: number | undefined;
  for (const copy of copies) {
    if (!clientRuns(copy)) continue;
    const at = Date.parse(copy.nextAttemptAt ?? '');
    if (Number.isNaN(at)) continue;
    if (soonest === undefined || at < soonest) soonest = at;
  }
  return soonest;
}

/**
 * Takes in what a run or a save changed: the open list, and — when something
 * reached Sent — that folder's counts and lists.
 */
export function applySentCopyRun(
  queryClient: QueryClient,
  accountId: string,
  result: SentCopyRunResult,
): void {
  const keys = queryKeys.account(accountId);
  queryClient.setQueryData(keys.sentCopies(), result.items);
  if (result.saved.length > 0) refreshSent(queryClient, accountId);
}

/** Sent gained a message: its counts and its list are stale. */
export function refreshSent(queryClient: QueryClient, accountId: string): void {
  const keys = queryKeys.account(accountId);
  void queryClient.invalidateQueries({ queryKey: keys.folders() });
  void queryClient.invalidateQueries({ queryKey: keys.messagesScope() });
}

/**
 * Called with every send's answer: a result that reports a kept copy means the
 * list is out of date, and re-reading it is what puts the banner up (and, for
 * a mailbox with no background credential, what schedules the retry).
 */
export function noteSendResult(queryClient: QueryClient, accountId: string, result: SendResult): void {
  if (result.sentCopy === undefined) return;
  void queryClient.invalidateQueries({ queryKey: queryKeys.account(accountId).sentCopies() });
}

/** Milliseconds from now until `at` (epoch ms); negative once it has passed. */
function msUntil(at: number): number {
  return at - Date.now();
}

/** How long before this mailbox may be run again; 0 when it may run now. */
function runWait(accountId: string): number {
  if (running.has(accountId)) return MIN_RUN_GAP_MS;
  return Math.max(0, (lastRun.get(accountId) ?? -Infinity) + MIN_RUN_GAP_MS - Date.now());
}

async function runFor(api: MailApi, queryClient: QueryClient, accountId: string): Promise<void> {
  running.add(accountId);
  lastRun.set(accountId, Date.now());
  try {
    applySentCopyRun(queryClient, accountId, await api.sentCopies.run(accountId));
  } catch {
    // The copy stays where it was and the list says so; the next refetch or
    // the next due time tries again. Nothing is lost by a run that failed.
  } finally {
    running.delete(accountId);
  }
}

/**
 * The open copies across `accounts`, oldest first within each mailbox. Only
 * mailboxes that can answer are asked: one whose password stopped working has
 * nothing to append with.
 */
export function useSentCopies(accounts: readonly Account[]): OpenSentCopy[] {
  const api = useApi();
  const queryClient = useQueryClient();
  const eligible = useMemo(
    () => accounts.filter((account) => account.isPrimary || account.status === 'connected'),
    [accounts],
  );

  const results = useQueries({
    queries: eligible.map((account) => ({
      queryKey: queryKeys.account(account.id).sentCopies(),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.sentCopies.list(account.id, signal),
      staleTime: SENT_COPIES_REFETCH_MS / 2,
      refetchInterval: SENT_COPIES_REFETCH_MS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      // A reminder, not a page: a failed read shows nothing and waits for the
      // next interval rather than climbing a retry ladder.
      retry: false,
    })),
  });

  // One stable signature of what is due, so the timers below are only reset
  // when a due time actually changed and not on every render.
  const lists = results.map((result) => result.data);
  const dueKey = eligible
    .map((account, index) => `${account.id}:${nextDue(lists[index] ?? []) ?? ''}`)
    .join('|');

  useEffect(() => {
    let cancelled = false;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (fn: () => void, ms: number) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (!cancelled) fn();
      }, Math.min(Math.max(ms, 0), MAX_TIMER_MS));
      timers.add(timer);
    };
    for (const entry of dueKey.split('|')) {
      const split = entry.lastIndexOf(':');
      const accountId = entry.slice(0, split);
      const due = Number(entry.slice(split + 1) || NaN);
      if (accountId === '' || Number.isNaN(due)) continue;
      // A run that changes the list changes `dueKey`, which replaces these
      // timers. One that changed nothing (it failed, or the copy is still due
      // by the server's clock) comes round again after the gap instead of
      // waiting for a list that will never look different.
      const attempt = (): void => {
        const wait = runWait(accountId);
        if (wait > 0) {
          later(attempt, wait);
          return;
        }
        void runFor(api, queryClient, accountId).then(() => later(attempt, MIN_RUN_GAP_MS));
      };
      later(attempt, msUntil(due));
    }
    return () => {
      cancelled = true;
      for (const timer of timers) clearTimeout(timer);
    };
  }, [api, queryClient, dueKey]);

  return eligible.flatMap((account, index) =>
    (lists[index] ?? []).map((copy) => ({ account, copy })),
  );
}
